import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  ApiFetch,
  GetPathPrefix,
  IsDevClient,
  OnAuthFailure,
  RefreshAccessToken,
} from '../utils/api';
import { RegisterServiceWorker } from '../utils/push';

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testMode, setTestMode] = useState(false);
  const [envName, setEnvName] = useState(null);

  const ClearSession = ({ keepUsername = false } = {}) => {
    localStorage.removeItem('riddler_token');
    if (!keepUsername) {
      localStorage.removeItem('riddler_username');
    }
    setUser(null);
  };

  const GoAuth = () => {
    const prefix = GetPathPrefix();
    window.location.assign(prefix ? `${prefix}/auth` : '/auth');
  };

  const GoHome = () => {
    const prefix = GetPathPrefix();
    window.location.assign(prefix ? `${prefix}/` : '/');
  };

  useEffect(() => {
    ApiFetch('/health')
      .then((health) => {
        setTestMode(Boolean(health?.test));
        if (health?.env) setEnvName(String(health.env));
      })
      .catch(() => {
        setTestMode(false);
      });

    const token = localStorage.getItem('riddler_token');
    if (!token) {
      setLoading(false);
      return;
    }

    ApiFetch('/auth/me')
      .then((me) => setUser(me))
      .catch(() => {
        ClearSession({ keepUsername: true });
      })
      .finally(() => setLoading(false));
  }, []);

  // Force login if JWT is gone or rejected mid-session (covers "Not authenticated").
  useEffect(() => {
    const off = OnAuthFailure(() => {
      ClearSession({ keepUsername: true });
      GoAuth();
    });
    return off;
  }, []);

  // Sliding refresh while the tab stays open (token is 72h; renew early anyway).
  useEffect(() => {
    if (!user) return undefined;
    const Tick = () => {
      RefreshAccessToken().catch(() => {});
    };
    Tick();
    const id = window.setInterval(Tick, 20 * 60 * 1000);
    return () => clearInterval(id);
  }, [user?.id]);

  // Warm the service worker so Web Push can deliver while another tab is focused.
  useEffect(() => {
    if (!user) return undefined;
    RegisterServiceWorker().catch(() => {});
    return undefined;
  }, [user?.id]);

  // Recover "user in React, token missing from storage" ghosts.
  useEffect(() => {
    if (!user) return undefined;
    const id = window.setInterval(() => {
      if (!localStorage.getItem('riddler_token')) {
        ClearSession({ keepUsername: true });
        GoAuth();
      }
    }, 5000);
    return () => clearInterval(id);
  }, [user?.id]);

  const PersistSession = (data) => {
    localStorage.setItem('riddler_token', data.access_token);
    localStorage.setItem('riddler_username', data.user.username);
    setUser(data.user);
    setError(null);
    return data;
  };

  /** Adopt a JWT minted outside /auth/login (e.g. Edward gate) without a full reload. */
  const AdoptToken = async (accessToken, usernameHint = '') => {
    const token = String(accessToken || '').trim();
    if (!token) throw new Error('Missing session token');
    localStorage.setItem('riddler_token', token);
    if (usernameHint) localStorage.setItem('riddler_username', String(usernameHint).trim());
    const me = await ApiFetch('/auth/me');
    setUser(me);
    setError(null);
    if (me?.username) localStorage.setItem('riddler_username', me.username);
    return me;
  };

  const register = async (pin, username, email) => {
    setError(null);
    try {
      const data = await ApiFetch('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ pin, username, email }),
      });
      return PersistSession(data);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const login = async (username, pin) => {
    setError(null);
    try {
      const data = await ApiFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, pin }),
      });
      return PersistSession(data);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = () => {
    ClearSession({ keepUsername: true });
  };

  const resetAccount = async () => {
    try {
      await ApiFetch('/auth/me', { method: 'DELETE' });
    } catch {
      /* best-effort: still clear local session */
    }
    ClearSession({ keepUsername: false });
    GoHome();
  };

  const nukeAllUsers = async () => {
    await ApiFetch('/admin/nuke', { method: 'POST' });
    ClearSession({ keepUsername: false });
    GoHome();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        testMode,
        envName,
        isDevEnv: IsDevClient(envName),
        register,
        login,
        AdoptToken,
        logout,
        resetAccount,
        nukeAllUsers,
        setError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
